import React from "react";
import clsx, { type ClassValue, } from "clsx";
import { twMerge, } from "tailwind-merge";

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

type MaxWidth = "xs" | "sm" | "md" | "lg" | "xl" | false;

// MUI v4 default: theme.breakpoints.values = { xs: 0, sm: 600, md: 960, lg: 1280, xl: 1920 }
// maxWidth aplica `Math.max(value, 444)` no breakpoint correspondente.
const maxWidthMap: Record<Exclude<MaxWidth, false>, string> = {
  xs: "max-w-[444px]",
  sm: "sm:max-w-[600px]",
  md: "md:max-w-[960px]",
  lg: "lg:max-w-[1280px]",
  xl: "xl:max-w-[1920px]",
};

// Quando `fixed`, define max-width como o valor do breakpoint a partir de cada ponto.
const fixedMap = [
  "sm:max-w-[600px]",
  "md:max-w-[960px]",
  "lg:max-w-[1280px]",
  "xl:max-w-[1920px]",
].join(" ");

// Props próprias do Container (independentes do elemento renderizado).
interface ContainerOwnProps {
  disableGutters?: boolean;
  fixed?: boolean;
  maxWidth?: MaxWidth;
  children?: React.ReactNode;
  className?: string;
}

// Componente polimórfico: as props extras vêm do elemento informado em `component`.
// Ex.: <Container component="form" onSubmit={...} /> -> onSubmit tipado como FormEventHandler<HTMLFormElement>.
export type ContainerProps<C extends React.ElementType = "div"> =
  ContainerOwnProps
  & { component?: C }
  & Omit<React.ComponentPropsWithRef<C>, keyof ContainerOwnProps | "component">;

type ContainerComponent = <C extends React.ElementType = "div">(
  props: ContainerProps<C>
) => React.ReactElement | null;

const Container = React.forwardRef(function Container<C extends React.ElementType = "div">(
  props: ContainerProps<C>,
  ref: React.ComponentPropsWithRef<C>["ref"]
) {
  const {
    className,
    component,
    disableGutters = false,
    fixed = false,
    maxWidth = "lg",
    ...other
  } = props as ContainerOwnProps & { component?: React.ElementType };

  const Component = (component ?? "div") as React.ElementType;

  const classes = cn(
    // Base: width 100%, margem horizontal auto, box-border, block
    "block box-border w-full mx-auto",

    // Gutters: padding horizontal de 16px (xs) e 24px (>= sm)
    !disableGutters && "px-4 sm:px-6",

    // Fixed: max-width acompanha cada breakpoint
    fixed && fixedMap,

    // maxWidth: limita largura no breakpoint correspondente
    !fixed && maxWidth !== false && maxWidthMap[maxWidth],

    className
  );

  return <Component ref={ref} className={classes} {...other} />;
}) as ContainerComponent & { displayName?: string };

Container.displayName = "Container";

export default Container;
